// @ts-nocheck
import React, { Fragment, useEffect, ReactElement } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Bar, Container } from "react-simple-resizer";
import { SecondaryNav } from "../components/navigation/SecondaryNav/SecondaryNav";
import ActionNav from "../components/navigation/ActionNav";
import { MainNav } from "../components/navigation/MainNav/MainNav";
import { Logotype } from "../components/logotype/Logotype";
import querystring from "query-string";
import { AuthService } from "../services/auth.service";
import { jwtDecode } from "jwt-decode";
import CollapsiblePanel from "../components/common/UI/CollapsiblePanel";
import { userLoggedIn } from "../services/api.auth.utils";

/**
 * AuthGuard - Handles authentication redirects
 * In v6, this is a component that checks auth and redirects if needed
 */
export const AuthGuard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = querystring.parse(location.search);
  const jwtTokenFromUrl = queryParams["accessToken"] as string | undefined;
  const fromLogin = queryParams["fromLogin"];
  const jwtTokenFromStorage = AuthService.token;

  useEffect(() => {
    // Handle token from URL (after login redirect)
    if (jwtTokenFromUrl) {
      // === POST-LOGIN: Check if refresh token survived page navigation ===
      let hasRefreshAfterRedirect = !!localStorage.getItem('refreshToken');

      // Recovery: if refresh token is missing from localStorage, try sessionStorage backup
      if (!hasRefreshAfterRedirect) {
        const backup = sessionStorage.getItem('__refresh_token_backup');
        if (backup) {
          console.log(
            '%c[AUTH GUARD] Recovering refresh token from sessionStorage backup',
            'background: #a50; color: #fff; font-size: 13px; padding: 3px 8px;'
          );
          localStorage.setItem('refreshToken', backup);
          hasRefreshAfterRedirect = true;
        }
      }
      // Clean up backup regardless
      sessionStorage.removeItem('__refresh_token_backup');

      console.log(
        `%c[AUTH GUARD] Token from URL (post-login redirect)`,
        hasRefreshAfterRedirect
          ? 'background: #080; color: #fff; font-size: 12px; padding: 2px 6px;'
          : 'background: #a00; color: #fff; font-size: 14px; padding: 4px 8px;',
        {
          hasRefreshToken: hasRefreshAfterRedirect,
          fromLogin: !!fromLogin,
          pathname: location.pathname,
          time: new Date().toISOString(),
        }
      );
      if (!hasRefreshAfterRedirect) {
        console.error(
          '%c[AUTH GUARD] ⚠️ NO refresh token after login redirect! Session will expire without renewal.',
          'background: #a00; color: #fff; font-size: 14px; padding: 4px 8px;'
        );
      }

      AuthService.token = jwtTokenFromUrl;

      // Remove token from URL but stay on current path
      delete queryParams.accessToken;
      delete queryParams.fromLogin;
      const urlWithoutToken =
        location.pathname + (Object.keys(queryParams).length ? "?" + querystring.stringify(queryParams) : "");

      if (fromLogin) {
        userLoggedIn();
      }

      // Only redirect if URL actually has query params to remove
      if (location.search) {
        navigate(urlWithoutToken, { replace: true });
      }
      return;
    }

    // Redirect unauthenticated users to login (except for public pages)
    const publicPaths = ["/privacy-policy", "/login", "/signup", "/forgot", "/failed-contact", "/iframe"];
    const isPublicPath = publicPaths.includes(location.pathname) ||
                         location.pathname.startsWith("/activate/") ||
                         location.pathname.startsWith("/reset-password/");

    if (!jwtTokenFromStorage && !isPublicPath) {
      // Only redirect if not already on login page to prevent infinite loop
      if (location.pathname !== "/login") {
        navigate("/login", { replace: true });
      }
      return;
    }

    // Redirect authenticated users from root or login page to welcome
    // But skip redirect if we're in the auth iframe
    if (jwtTokenFromStorage && (location.pathname === "/" || location.pathname === "/login") && location.pathname !== "/iframe") {
      // Only redirect if not already on welcome page to prevent infinite loop
      if (location.pathname !== "/welcome") {
        navigate("/welcome", { replace: true });
      }
    }
  }, [jwtTokenFromUrl, jwtTokenFromStorage, location.pathname, fromLogin, navigate, location.search]);

  return null;
};

/**
 * PrivateRoute - Wrapper for protected routes
 * In v6, we use this as a layout component that wraps protected content
 */
interface PrivateRouteProps {
  children: ReactElement;
  extras?: {
    withoutTree?: boolean;
    hasSecondaryMenu?: boolean;
  };
}

export const PrivateRoute = ({ children, extras = {} }: PrivateRouteProps) => {
  const location = useLocation();
  const params = useParams();
  const userToken = AuthService.token;
  const dividerBar = <Bar size={3} style={{ background: "#888888", cursor: "col-resize" }} />;

  // Check for privacy policy (accessible without auth)
  const isPrivacyPolicy = location.pathname === "/privacy-policy";

  // Validate token — but be careful not to trigger logout during an in-flight
  // token refresh. The API service's pre-flight interceptor handles refresh
  // automatically; we only force logout when there's truly no session left.
  let isTokenValid = !!userToken;
  if (userToken) {
    try {
      const decoded = jwtDecode<{ exp: number }>(userToken);
      if (decoded.exp * 1000 < Date.now()) {
        // Token expired — check if refresh token exists for recovery
        if (!AuthService.refreshToken) {
          // No refresh token either — session is truly gone
          isTokenValid = false;
          AuthService.globalLogout();
        }
        // If refresh token exists, keep isTokenValid=true and let the API
        // interceptor handle the refresh on the next API call.
        // DON'T set isTokenValid=false here — that would redirect to login
        // and abort the in-flight refresh.
      }
    } catch (e) {
      // Token is malformed — but check if a refresh token exists first.
      // During token rotation the old token may briefly be invalid.
      if (!AuthService.refreshToken) {
        isTokenValid = false;
        AuthService.globalLogout();
      }
      // If refresh token exists, stay on the page — the interceptor will fix it.
    }
  }

  // If authenticated with a valid token, render the protected content with layout
  if (isTokenValid) {
    return (
      <div className="main-container">
        <div id="header">
          <Logotype />
          <MainNav />
          <ActionNav />
        </div>
        <div className="main-content">
          {!extras.withoutTree && (
            <Fragment>
              {extras.hasSecondaryMenu ? (
                <div className="aside">
                  <SecondaryNav />
                </div>
              ) : (
                <div id="aside" className="aside">
                  <CollapsiblePanel />
                </div>
              )}
            </Fragment>
          )}
          <div id="content" className="content">
            {children}
          </div>
        </div>
        <Container>{dividerBar}</Container>
      </div>
    );
  }

  // Privacy policy is accessible without auth
  if (isPrivacyPolicy) {
    return (
      <div className="main-container">
        <div className="main-content h-100">
          {!extras.withoutTree && (
            <Fragment>
              {extras.hasSecondaryMenu ? (
                <div className="aside">
                  <SecondaryNav />
                </div>
              ) : (
                <div id="aside" className="aside">
                  <CollapsiblePanel />
                </div>
              )}
            </Fragment>
          )}
          <div id="content" className="content">
            {children}
          </div>
        </div>
        <Container>{dividerBar}</Container>
      </div>
    );
  }

  // Not authenticated, redirect to login
  return <Navigate to="/" state={{ from: location }} replace />;
};

/**
 * ProtectedLayout - Layout wrapper for all protected routes
 * Used with Outlet for nested routing
 */
interface ProtectedLayoutProps {
  extras?: {
    withoutTree?: boolean;
    hasSecondaryMenu?: boolean;
  };
}

export const ProtectedLayout = ({ extras = {} }: ProtectedLayoutProps) => {
  const location = useLocation();
  const userToken = AuthService.token;
  const dividerBar = <Bar size={3} style={{ background: "#888888", cursor: "col-resize" }} />;

  // Validate token — same race-condition-safe logic as PrivateRoute.
  // Don't call globalLogout() if a refresh token exists (refresh may be in-flight).
  let isTokenValid = !!userToken;
  if (userToken) {
    try {
      const decoded = jwtDecode<{ exp: number }>(userToken);
      if (decoded.exp * 1000 < Date.now()) {
        if (!AuthService.refreshToken) {
          isTokenValid = false;
          AuthService.globalLogout();
        }
        // Refresh token exists — let API interceptor handle refresh
      }
    } catch (e) {
      if (!AuthService.refreshToken) {
        isTokenValid = false;
        AuthService.globalLogout();
      }
      // Refresh token exists — let API interceptor handle refresh
    }
  }

  if (!isTokenValid) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <div className="main-container">
      <div id="header">
        <Logotype />
        <MainNav />
        <ActionNav />
      </div>
      <div className="main-content">
        {!extras.withoutTree && (
          <Fragment>
            {extras.hasSecondaryMenu ? (
              <div className="aside">
                <SecondaryNav />
              </div>
            ) : (
              <div id="aside" className="aside">
                <CollapsiblePanel />
              </div>
            )}
          </Fragment>
        )}
        <div id="content" className="content">
          <Outlet />
        </div>
      </div>
      <Container>{dividerBar}</Container>
    </div>
  );
};

/**
 * PublicLayout - Layout for public routes (no auth required)
 */
export const PublicLayout = () => {
  return <Outlet />;
};
